import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  MdPrint,
  MdRefresh,
  MdRemove,
  MdAdd,
  MdLabel,
  MdPhone,
  MdEmail,
  MdLanguage,
} from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { brandFor } from "../businessBranding";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { useBranding } from "../theme/BrandingProvider";
import LabelPrintModal from "../components/LabelPrintModal";
import fallbackLogo from "../images/logo.png";
import "../work-orders-print.css";

/* ------------------------------------------------------------------ *
 * Single work order (print) — /workorder/print/:id/:date
 *
 * Replaces the "workorder" branch of pages/Print.js. Same URL (order id
 * plus the hyphenated delivery date), same endpoint: GET /wo/getWo/:id.
 *
 * Three things the old page did, all kept:
 *   - the work order sheet: order details, comments, payment, prices and
 *     a product checklist with florist / quality controller tick boxes;
 *   - the funeral slip (A5 landscape) for funeral orders;
 *   - label printing — components/LabelPrintModal (PDF generation +
 *     PrintNode printers, same endpoints as the old popup).
 * ------------------------------------------------------------------ */

const PAPERS = [
  { id: "a4-portrait", label: "A4 portrait", page: "A4 portrait" },
  { id: "a4-landscape", label: "A4 landscape", page: "A4 landscape" },
];
const SLIP_PAGE = "A5 landscape";
const FONT_MIN = 9;
const FONT_MAX = 18;
const FONT_DEFAULT = 12;
const STORAGE_KEY = "rf-wo1p-prefs";

/* The slip carries the shop's contact line. Settings has no contact
   fields, so it is keyed on the business here — an unknown business gets
   a slip with its name and no contact line rather than someone else's. */
const CONTACTS = {
  raysflorist: {
    tagline: "Making Funerals Beautiful",
    phone: "02 9737 8877",
    email: "info@raysflorist.com.au",
    web: "www.raysflorist.com.au",
  },
};
const normBiz = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};
const locName = (raw) => (safeParse(raw, {}) || {}).name || "";
const money = (n) => "$" + Number(n || 0).toFixed(2);

function readPrefs() {
  try {
    return safeParse(localStorage.getItem(STORAGE_KEY), {}) || {};
  } catch (e) {
    return {};
  }
}
function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    /* private mode */
  }
}

/* What the label popup needs — same derivation as the legacy page. */
function labelDetailsOf(o) {
  const d = {
    type: o.type,
    latePerson: (o.late_person_name || "Late Person Name - N/A").toUpperCase(),
    funeral_date: o.funeral_date || "N/A",
    comment: (o.order_comment || "Comment - N/A").toUpperCase(),
    productCode: (o.serial_no || "Code - N/A").toUpperCase(),
    tributes: safeParse(o.tributes_selected, []) || [],
    name: o.recipient_name || " ",
    signoff: "Regards",
    card_message: o.card_message || "Card Message - N/A",
    recipient: o.recipient_name || "Recipient Name - N/A",
    location: locName(o.delivery_location) || "Location - N/A",
    phoneNumber: o.recipient_phone || "Contact - N/A",
  };
  Object.keys(d).forEach((k) => {
    if (typeof d[k] === "string" && !["card_message", "comment"].includes(k))
      d[k] = d[k].replace(/(\r\n|\n|\r)/gm, "");
  });
  return d;
}

const Val = ({ children, className }) =>
  children === "" || children == null ? (
    <span className={"wop-empty " + (className || "")}>—</span>
  ) : (
    <span className={className}>{children}</span>
  );

function Field({ k, children, wide, className }) {
  return (
    <div className={"wop-wo-field " + (wide ? "is-wide " : "") + (className || "")}>
      <span className="wop-wo-k">{k}</span>
      <span className="wop-wo-v">
        <Val>{children}</Val>
      </span>
    </div>
  );
}

const Box = ({ label }) => (
  <span className="wop-box" aria-label={label} title={label} />
);

function ProductRows({ items, groupLabel, count }) {
  return (
    <>
      <tr className="wop-wo-group">
        <td colSpan={4}>
          {groupLabel}
          <span className="wop-wo-group-n">{count}</span>
        </td>
        <td className="wop-center">
          <Box label={`${groupLabel} florist check`} />
        </td>
        <td className="wop-center">
          <Box label={`${groupLabel} quality controller check`} />
        </td>
      </tr>
      {items.length === 0 && (
        <tr>
          <td colSpan={6} className="wop-wo-none">
            None on this order.
          </td>
        </tr>
      )}
      {items.map((p, i) => {
        const price = p.customPrice || p.price;
        const notes = [
          p.lidType ? { k: "Lid type", v: p.lidType, tone: "is-red" } : null,
          p.comment ? { k: "Comment", v: p.comment, tone: "is-red" } : null,
          p.sash_message ? { k: "Sash", v: p.sash_message, tone: "is-grey" } : null,
        ].filter(Boolean);
        return (
          <tr key={p._id || i}>
            <td className="wop-center wop-wo-qty">{p.amount}</td>
            <td>
              <span className="wop-wo-prod">
                {(p.product_name || p.product_type || "Product").toUpperCase()}
                {p.code && (p.product_type || "").toLowerCase() === "casket cover" && (
                  <span className="wop-chip is-cc wop-wo-code">{p.code}</span>
                )}
              </span>
              {notes.length > 0 && (
                <span className="wop-wo-notes">
                  {notes.map((n) => (
                    <span key={n.k} className={"wop-wo-note " + n.tone}>
                      <span className="wop-wo-note-k">{n.k}</span>
                      {n.v}
                    </span>
                  ))}
                </span>
              )}
            </td>
            <td className="wop-center">
              <Val>{(p.size || "").toUpperCase()}</Val>
            </td>
            <td className="wop-right wop-wo-price">{price != null && price !== "" ? money(price) : <span className="wop-empty">—</span>}</td>
            <td className="wop-center">
              <Box label="Florist check" />
            </td>
            <td className="wop-center">
              <Box label="Quality controller check" />
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default function WorkOrderPrintPage() {
  const { filter: orderId, date: hyphenDate } = useParams();
  const { business: tokenBusiness, user } = useAuth();
  /* Name, logo and palette follow the business that is open — the same
     one the shell and the :root theme use — falling back to the token's. */
  const activeBusiness = useActiveBusiness();
  const business =
    activeBusiness && (activeBusiness.name || activeBusiness.code)
      ? activeBusiness
      : tokenBusiness || activeBusiness;
  useBranding(business); // re-render when its logo or palette is saved

  const prefs = useMemo(readPrefs, []);
  const [font, setFont] = useState(() =>
    Math.min(FONT_MAX, Math.max(FONT_MIN, Number(prefs.font) || FONT_DEFAULT))
  );
  const [paper, setPaper] = useState(() =>
    PAPERS.some((p) => p.id === prefs.paper) ? prefs.paper : PAPERS[0].id
  );
  useEffect(() => {
    writePrefs({ font, paper });
  }, [font, paper]);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [mode, setMode] = useState("sheet"); // "sheet" | "slip"

  /* label modal — details derived from the order, edits kept while open */
  const [labelInfo, setLabelInfo] = useState({});
  const [labelsOpen, setLabelsOpen] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setError("This link has no work order in it. Open it from the work orders page.");
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError("");
    axios
      .get(BASE_SERVER_URL + "/wo/getWo/" + orderId)
      .then((doc) => {
        if (!alive) return;
        const found = doc.data.status === "success" && (doc.data.response || [])[0];
        if (found) {
          setOrder(found);
          setLabelInfo(labelDetailsOf(found));
        } else setError(doc.data.message || "No work order with this id was found.");
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Could not reach the server. Check your connection and try again."
        );
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderId, reloadTick]);

  const o = useMemo(() => order || {}, [order]);
  const funeral = o.type === "Funeral";
  useEffect(() => {
    if (!funeral && mode === "slip") setMode("sheet");
  }, [funeral, mode]);

  const casketCovers = useMemo(() => safeParse(o.casket_cover_selected, []) || [], [o]);
  const tributes = useMemo(() => safeParse(o.tributes_selected, []) || [], [o]);
  const products = useMemo(() => safeParse(o.products_selected, []) || [], [o]);
  const price = o.price_detail || {};
  const floral = Number(price.total_price || 0);
  const deliveryFee = Number(price.delivery_fee || 0);
  const total = floral + deliveryFee;
  const unpaid = o.payment_option === "**UNPAID**";
  const itemCount = (funeral ? casketCovers.concat(tributes) : products).reduce(
    (n, p) => n + (Number(p.amount) || 0),
    0
  );

  const funeralDay = moment(o.funeral_date || "", "DD/MM/YYYY", true);
  const deliveryDay = moment(hyphenDate || "", "DD-MM-YYYY", true);
  const keyDay = funeral ? funeralDay : deliveryDay;
  const keyDayLong = keyDay.isValid()
    ? keyDay.format("dddd D MMMM YYYY")
    : (funeral ? o.funeral_date : (hyphenDate || "").replace(/-/g, "/")) || "—";

  const brand = brandFor(business);
  const businessName = (business && business.name) || "Ray's Florist";
  const contact = CONTACTS[normBiz(business && business.name)] || CONTACTS[normBiz(business && business.code)] || null;
  const printedBy = (user && (user.name || user.full_name || user.username || user.email)) || "";
  const paperDef = PAPERS.find((p) => p.id === paper) || PAPERS[0];
  const pageSize = mode === "slip" ? SLIP_PAGE : paperDef.page;
  const recipient = funeral ? o.late_person_name : o.recipient_name;

  useEffect(() => {
    document.title = o.serial_no ? `Work order #${o.serial_no}` : "Work order";
  }, [o.serial_no]);

  const stats = [
    { k: "Type", v: o.type || "—" },
    { k: "Items", v: itemCount },
    { k: "Total", v: money(total) },
    { k: "Payment", v: unpaid ? "UNPAID" : o.payment_option || "—", tone: unpaid ? "is-red" : "" },
  ];

  const canLabel = Boolean(order) && !loading && !error;

  return (
    <div className={"wop-page is-" + (mode === "slip" ? "slip" : paper)}>
      <style>{`@page { size: ${pageSize}; margin: ${mode === "slip" ? "6mm" : "9mm 10mm"}; }`}</style>

      {labelsOpen && (
        <div className="no-print">
          <LabelPrintModal
            detail={labelInfo}
            subtitle={`#${o.serial_no || "—"} · ${recipient || "—"}`}
            onChange={(patch) => setLabelInfo((prev) => ({ ...prev, ...patch }))}
            onClose={() => setLabelsOpen(false)}
          />
        </div>
      )}

      <header className="wop-toolbar no-print">
        <div className="wop-tb-title">
          <span className="wop-tb-kicker">Print · Work order</span>
          <span className="wop-tb-date">
            {loading ? "Loading…" : o.serial_no ? `#${o.serial_no} · ${recipient || "—"}` : "Work order"}
          </span>
        </div>

        <div className="wop-tb-stats">
          {stats.map((s) => (
            <span key={s.k} className="wop-stat">
              <span className={"wop-stat-v " + (s.tone || "")}>{loading ? "…" : s.v}</span>
              <span className="wop-stat-k">{s.k}</span>
            </span>
          ))}
        </div>

        <div className="wop-tb-controls">
          {funeral && (
            <label className="wop-ctrl">
              <span className="wop-ctrl-k">Layout</span>
              <span className="wop-segmented" role="radiogroup" aria-label="Layout">
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "sheet"}
                  className={mode === "sheet" ? "is-on" : ""}
                  onClick={() => setMode("sheet")}
                >
                  Work order
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "slip"}
                  className={mode === "slip" ? "is-on" : ""}
                  onClick={() => setMode("slip")}
                >
                  Funeral slip
                </button>
              </span>
            </label>
          )}

          <label className="wop-ctrl">
            <span className="wop-ctrl-k">Text size</span>
            <span className="wop-stepper">
              <button
                type="button"
                aria-label="Smaller text"
                disabled={font <= FONT_MIN}
                onClick={() => setFont((f) => Math.max(FONT_MIN, f - 1))}
              >
                <MdRemove />
              </button>
              <span className="wop-stepper-v">{font}</span>
              <button
                type="button"
                aria-label="Larger text"
                disabled={font >= FONT_MAX}
                onClick={() => setFont((f) => Math.min(FONT_MAX, f + 1))}
              >
                <MdAdd />
              </button>
            </span>
          </label>

          {mode === "sheet" && (
            <label className="wop-ctrl">
              <span className="wop-ctrl-k">Paper</span>
              <span className="wop-segmented" role="radiogroup" aria-label="Paper size">
                {PAPERS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={paper === p.id}
                    className={paper === p.id ? "is-on" : ""}
                    onClick={() => setPaper(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </span>
            </label>
          )}

          <button
            type="button"
            className="wop-btn is-ghost"
            onClick={() => setLabelsOpen(true)}
            disabled={!canLabel}
            title="Preview and print the order's labels"
          >
            <MdLabel /> Labels
          </button>
          <button
            type="button"
            className="wop-btn is-ghost"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            title="Reload the work order"
          >
            <MdRefresh /> Refresh
          </button>
          <button
            type="button"
            className="wop-btn is-primary"
            onClick={() => window.print()}
            disabled={loading || Boolean(error)}
          >
            <MdPrint /> {mode === "slip" ? "Print slip" : "Print"}
          </button>
        </div>
      </header>

      {mode === "slip" ? (
        <main className="wop-slip" style={{ "--wop-font": font + "px" }}>
          <div className="wop-slip-head">
            <img src={brand.logo || fallbackLogo} alt={businessName} className="wop-logo" />
            <span className="wop-slip-biz">{businessName}</span>
            {contact && <span className="wop-slip-tag">{contact.tagline}</span>}
          </div>
          <div className="wop-slip-body">
            <div className="wop-slip-row">
              <span className="wop-slip-k">Funeral name</span>
              <span className="wop-slip-v"><Val>{o.late_person_name}</Val></span>
            </div>
            <div className="wop-slip-row">
              <span className="wop-slip-k">Funeral location</span>
              <span className="wop-slip-v"><Val>{locName(o.funeral_location)}</Val></span>
            </div>
            <div className="wop-slip-row">
              <span className="wop-slip-k">Funeral director</span>
              <span className="wop-slip-v"><Val>{(safeParse(o.fd_code, {}) || {}).code}</Val></span>
            </div>
            <div className="wop-slip-row">
              <span className="wop-slip-k">Funeral date &amp; time</span>
              <span className="wop-slip-v">
                <Val>
                  {funeralDay.isValid()
                    ? `${funeralDay.format("dddd D MMM YYYY")}${o.funeral_time ? " at " + o.funeral_time : ""}`
                    : [o.funeral_date, o.funeral_time].filter(Boolean).join(" at ")}
                </Val>
              </span>
            </div>
          </div>
          {contact && (
            <div className="wop-slip-contact">
              <span><MdPhone /> {contact.phone}</span>
              <span><MdEmail /> {contact.email}</span>
              <span><MdLanguage /> {contact.web}</span>
            </div>
          )}
        </main>
      ) : (
        <main className="wop-sheet wop-wo" style={{ "--wop-font": font + "px" }}>
          <div className="wop-head">
            <div className="wop-brand">
              <img src={brand.logo || fallbackLogo} alt={businessName} className="wop-logo" />
            </div>
            <div className="wop-head-mid">
              <h1 className="wop-title">
                Work Order <span className="wop-wo-serial">#{o.serial_no || "—"}</span>
              </h1>
              <span className="wop-subtitle">
                {o.type ? `${o.type} order` : "Order"} · {businessName}
              </span>
            </div>
            <div className="wop-head-date">
              <span className="wop-date-dow">{funeral ? "Funeral" : "Delivery"}</span>
              <span className="wop-date-main">{loading ? "…" : keyDayLong}</span>
            </div>
          </div>

          {loading ? (
            <div className="wop-wo-loading">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="wop-skel" />
              ))}
            </div>
          ) : error ? (
            <div className="wop-state is-error wop-wo-state">
              <strong>Nothing to print.</strong> {error}
              {orderId && (
                <button
                  type="button"
                  className="wop-btn is-ghost no-print"
                  onClick={() => setReloadTick((t) => t + 1)}
                >
                  <MdRefresh /> Try again
                </button>
              )}
            </div>
          ) : (
            <>
              <section className="wop-wo-section">
                <h2 className="wop-wo-h">{funeral ? "Funeral" : "Delivery"}</h2>
                <div className="wop-wo-grid">
                  {funeral ? (
                    <>
                      <Field k="Recipient" className="is-strong">{o.late_person_name}</Field>
                      <Field k="Funeral director">{(safeParse(o.fd_code, {}) || {}).code}</Field>
                      <Field k="Funeral location" wide>{locName(o.funeral_location)}</Field>
                      <Field k="Funeral date">{funeralDay.isValid() ? funeralDay.format("dddd D MMMM YYYY") : o.funeral_date}</Field>
                      <Field k="Funeral time" className="is-strong">{o.funeral_time}</Field>
                      <Field k="CC delivery" wide>{locName(o.casket_cover_delivery_location)}</Field>
                      <Field k="CC delivery time" className="is-strong">{o.casket_cover_delivery_time}</Field>
                    </>
                  ) : (
                    <>
                      <Field k="Recipient" className="is-strong">{o.recipient_name}</Field>
                      <Field k="Recipient phone">{o.recipient_phone}</Field>
                      <Field k="Delivery location" wide>{locName(o.delivery_location)}</Field>
                      <Field k="Delivery area">{o.delivery_area}</Field>
                      <Field k="Delivery date">{deliveryDay.isValid() ? deliveryDay.format("dddd D MMMM YYYY") : (hyphenDate || "").replace(/-/g, "/")}</Field>
                      <Field k="Delivery time" className="is-strong">{o.time_of_delivery}</Field>
                      <Field k="Company">{o.company_name}</Field>
                      <Field k="Authority to leave">
                        <span className={"wop-wo-flag " + (o.autority_to_leave ? "is-yes" : "is-no")}>
                          {o.autority_to_leave ? "Yes" : "No"}
                        </span>
                      </Field>
                    </>
                  )}
                </div>
              </section>

              <section className="wop-wo-section">
                <h2 className="wop-wo-h">Order</h2>
                <div className="wop-wo-grid">
                  <Field k="Ordered by">{o.ordered_by}</Field>
                  <Field k="Phone">{o.orderer_phone}</Field>
                  <Field k="Payment method">
                    <span className={unpaid ? "wop-wo-flag is-no" : ""}>{unpaid ? "UNPAID" : o.payment_option}</span>
                  </Field>
                  <Field k="Invoice email">{o.payment_email}</Field>
                </div>
                <div className="wop-wo-messages">
                  <div className="wop-wo-msg">
                    <span className="wop-wo-k">Order comment</span>
                    <span className="wop-wo-msg-v"><Val>{o.order_comment}</Val></span>
                  </div>
                  <div className="wop-wo-msg is-card">
                    <span className="wop-wo-k">Card message</span>
                    <span className="wop-wo-msg-v"><Val>{o.card_message}</Val></span>
                  </div>
                </div>
              </section>

              <section className="wop-wo-section">
                <h2 className="wop-wo-h">Products</h2>
                <table className="wop-table wop-wo-products">
                  <colgroup>
                    <col style={{ width: "6%" }} />
                    <col />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="wop-center">Qty</th>
                      <th>Product</th>
                      <th className="wop-center">Size</th>
                      <th className="wop-right">Price</th>
                      <th className="wop-center">Florist</th>
                      <th className="wop-center">Quality Controller</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funeral ? (
                      <>
                        <ProductRows items={casketCovers} groupLabel="Casket covers" count={casketCovers.length} />
                        <ProductRows items={tributes} groupLabel="Tributes" count={tributes.length} />
                      </>
                    ) : (
                      <ProductRows items={products} groupLabel="Products" count={products.length} />
                    )}
                  </tbody>
                </table>
              </section>

              <div className="wop-wo-totals">
                <span className="wop-wo-total">
                  <span className="wop-wo-k">Floral value</span>
                  <span className="wop-wo-total-v">{money(floral)}</span>
                </span>
                <span className="wop-wo-total">
                  <span className="wop-wo-k">Delivery fee</span>
                  <span className="wop-wo-total-v">{money(deliveryFee)}</span>
                </span>
                <span className="wop-wo-total is-grand">
                  <span className="wop-wo-k">Total order value</span>
                  <span className="wop-wo-total-v">{money(total)}</span>
                </span>
              </div>

              <div className="wop-wo-signoff">
                <span className="wop-wo-sign">
                  <span className="wop-wo-k">Final check</span>
                  <Box label="Final check" />
                </span>
                <span className="wop-wo-sign is-line">
                  <span className="wop-wo-k">Sighted by</span>
                  <span className="wop-run-line" />
                </span>
              </div>
            </>
          )}

          <footer className="wop-foot">
            <span>
              {businessName} · Work order {o.serial_no ? `#${o.serial_no}` : ""}
            </span>
            <span>
              Printed {moment().format("D MMM YYYY, h:mm a")}
              {printedBy ? ` by ${printedBy}` : ""}
            </span>
          </footer>
        </main>
      )}
    </div>
  );
}
